<script setup lang="ts">
import { SancareOcticon } from '@sancare/typescript-commons'
import SancareAccordionItem from '@sancare/typescript-commons/src/design-system/molecules/SancareAccordionItem.vue'
import { HealthDataDocument } from '@sancare/typescript-commons/src/types/health-data'
import { reactive } from 'vue'
import { useI18n } from 'vue-i18n'

import { FalseJustificationType } from '@/false-justifications/types'
import { FalseJustificationItem, FlagJustificationPayload, TextualJustificationChunk } from '@/misc/types'

import { TextualPart } from './justification-parts'

const { t } = useI18n()

const FALSE_JUSTIFICATION_SIGNALS: { errorType: FalseJustificationType, label: string, icon: string }[] = [
  { errorType: FalseJustificationType.NEG, label: t('stayDisplayer.justificationSummary.flagTooltip.negation'), icon: 'circle-slash' },
  { errorType: FalseJustificationType.ANT, label: t('stayDisplayer.justificationSummary.flagTooltip.antecedent'), icon: 'history' },
  { errorType: FalseJustificationType.DBT, label: t('stayDisplayer.justificationSummary.flagTooltip.doubt'), icon: 'question' },
  { errorType: FalseJustificationType.REC, label: t('stayDisplayer.justificationSummary.flagTooltip.recurrent'), icon: 'sync' },
]

const props = withDefaults(defineProps<{
  parts: TextualPart[]
  // Whether the false-justification signalling icons are offered (only for the passages of the
  // suggestion itself, not for the ones of one evaluation)
  canFlag?: boolean
  labelType?: string | null
  labelReference?: string | null
}>(), {
  canFlag: false,
  labelType: null,
  labelReference: null,
})

const emit = defineEmits<{
  'select-health-data': [mainContent: HealthDataDocument, scrollTarget?: { docId: number, offset: number | null }, event?: MouseEvent]
  'flag-justification': [payload: FlagJustificationPayload]
  'unflag-justification': [payload: { justificationId: number, docType: string, docId: number }]
}>()

// Keyed by part.key, defaulting (via SancareAccordionItem's own default) to expanded — matches
// the pre-accordion behavior where every document's content was always visible.
const expandedParts = reactive<Record<string, boolean>>({})

// A paragraph only ever mixes relevant (colored) chunks with filler (null-color) text trimmed around them
// — flagging operates on the relevant ones, mirroring JustificationSummary.vue's per-paragraph behavior.
function relevantChunks(paragraph: TextualJustificationChunk[]): TextualJustificationChunk[] {
  return paragraph.filter((chunk) => chunk.color !== null)
}

function findFalseJustification(part: TextualPart, chunk: TextualJustificationChunk): FalseJustificationItem | null {
  return part.falseJustifications.find(
    (fj) => fj.hintStart === chunk.offsetStart && fj.hintEnd === chunk.offsetStart + chunk.text.length
  ) ?? null
}

function hasUnflaggedChunk(part: TextualPart, paragraph: TextualJustificationChunk[]): boolean {
  return relevantChunks(paragraph).some((chunk) => findFalseJustification(part, chunk) === null)
}

function hasFlaggedChunk(part: TextualPart, paragraph: TextualJustificationChunk[]): boolean {
  return relevantChunks(paragraph).some((chunk) => findFalseJustification(part, chunk) !== null)
}

function flagParagraph(part: TextualPart, paragraph: TextualJustificationChunk[], errorType: FalseJustificationType) {
  if (props.labelType === null || props.labelReference === null) {
    return
  }
  for (const chunk of relevantChunks(paragraph)) {
    if (findFalseJustification(part, chunk) === null) {
      emit('flag-justification', {
        errorType,
        docType: part.docType,
        docId: part.docId,
        hintStart: chunk.offsetStart,
        hintEnd: chunk.offsetStart + chunk.text.length,
        labelType: props.labelType,
        labelReference: props.labelReference,
      })
    }
  }
}

function unflagParagraph(part: TextualPart, paragraph: TextualJustificationChunk[]) {
  for (const chunk of relevantChunks(paragraph)) {
    const falseJustification = findFalseJustification(part, chunk)
    if (falseJustification !== null) {
      emit('unflag-justification', { justificationId: falseJustification.id, docType: part.docType, docId: part.docId })
    }
  }
}

// A chunk already flagged locally (healthData.falseJustifications, updated in place by the
// createFalseJustification*/deleteFalseJustification mutations) takes priority over the server-computed
// color, so the popin reflects a flag/unflag immediately without waiting on a re-fetch.
function chunkClasses(part: TextualPart, chunk: TextualJustificationChunk): string {
  if (findFalseJustification(part, chunk) !== null || chunk.color === 'red') {
    return 'health-data-reported__clickable'
  }
  if (chunk.color === 'blue') {
    return 'health-data-keyword__clickable'
  }
  return ''
}

function handlePartSelection(part: TextualPart, event?: MouseEvent) {
  emit('select-health-data', { type: part.mainContentType, targetId: part.docId }, { docId: part.docId, offset: null }, event)
}

function handleChunkSelection(part: TextualPart, chunk: TextualJustificationChunk, event?: MouseEvent) {
  emit('select-health-data', { type: part.mainContentType, targetId: part.docId }, { docId: part.docId, offset: chunk.offsetStart }, event)
}
</script>

<template>
  <div>
    <div
      v-for="part in parts"
      :key="part.key"
      class="m-3"
    >
      <sancare-accordion-item
        v-model:is-expanded="expandedParts[part.key]"
        :title="part.title"
        toggle-target="arrow"
        @title-click="handlePartSelection(part, $event)"
      >
        <template
          v-for="(paragraph, paragraphIdx) in part.paragraphs"
          :key="paragraphIdx"
        >
          <div
            v-if="paragraphIdx === 0 && part.startTruncated"
            class="py-2 ps-3 justification-ellipsis"
          >
            [...]
          </div>
          <div class="d-flex align-items-start p-3 gap-3">
            <div class="flex-grow-1">
              <span
                v-for="(chunk, chunkIdx) in paragraph"
                :key="chunkIdx"
                :class="chunkClasses(part, chunk)"
                class="text-data-content"
                @click="handleChunkSelection(part, chunk, $event)"
                @click.middle="handleChunkSelection(part, chunk, $event)"
              >{{ chunk.text }}</span>
            </div>
            <div
              v-if="canFlag"
              class="justification-flags gap-1"
            >
              <template v-if="hasUnflaggedChunk(part, paragraph)">
                <span
                  v-for="signal in FALSE_JUSTIFICATION_SIGNALS"
                  :key="signal.errorType"
                  v-tooltip="signal.label"
                  @click="flagParagraph(part, paragraph, signal.errorType)"
                >
                  <sancare-octicon
                    class="btn btn-icon justification-flag-icon"
                    :name="signal.icon"
                    :width="20"
                    :height="20"
                  />
                </span>
              </template>
              <span
                v-if="hasFlaggedChunk(part, paragraph)"
                v-tooltip="t('stayDisplayer.justificationSummary.unflagTooltip')"
                @click="unflagParagraph(part, paragraph)"
              >
                <sancare-octicon
                  class="btn btn-icon justification-flag-icon"
                  name="trash"
                  :width="20"
                  :height="20"
                />
              </span>
            </div>
          </div>
          <div
            v-if="paragraphIdx < part.paragraphs.length - 1"
            class="py-2 ps-3 justification-ellipsis"
          >
            [...]
          </div>
          <div
            v-if="paragraphIdx === part.paragraphs.length - 1 && part.endTruncated"
            class="py-2 ps-3 justification-ellipsis rounded-bottom-2"
          >
            [...]
          </div>
        </template>
      </sancare-accordion-item>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@sancare/typescript-commons/src/assets/styles/variables';
@import '@/design-system/tokens/colors';

// Same light blue as the app header's background (SancareHeader.vue), to signal an
// omitted-text placeholder without competing with .health-data-keyword__clickable's highlight blue.

.justification-ellipsis {
  background: $bg-primary-subtle;
}

.justification-flags {
  display: grid;
  grid-template-columns: repeat(2, auto);
  justify-content: end;
}

// .btn-icon (button.scss) targets a `svg.octicon` descendant, but SancareOcticon puts its
// fallthrough classes directly on the root <svg> — so that rule never actually matches here,
// leaving the icons on the SVG's default black fill.

.justification-flag-icon {
  fill: $icon-subtle-purple;
}
</style>
